import { Add } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import axios from 'axios'
import { useEffect, useState } from 'react'
import { ConfirmDialog } from '../components/shared/ConfirmDialog'
import '../components/styles/ListPage.css'

interface SupplierOption {
  id: number
  name: string
}

interface SupplierRowDraft {
  id: string
  supplierId: number | null
  name: string
  hasLoa: boolean
  loaLink: string
}

export interface ManufacturerSuppliersDrawerProps {
  open: boolean
  onClose: () => void
  manufacturerId: number | null
}

export function ManufacturerSuppliersDrawer({ open, onClose, manufacturerId }: ManufacturerSuppliersDrawerProps) {
  const [rows, setRows] = useState<SupplierRowDraft[]>([])
  const [options, setOptions] = useState<SupplierOption[]>([])
  const [deleteTarget, setDeleteTarget] = useState<SupplierRowDraft | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open || manufacturerId === null) return

    setRows([])
    setLoading(true)

    Promise.all([
      axios.get<SupplierOption[]>(`/manufacturer/${manufacturerId}/suppliers`),
      axios.get<{ count: number; list: SupplierOption[] }>('/supplier'),
    ])
      .then(([currentRes, optionsRes]) => {
        setRows(
          [...currentRes.data]
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
            .map(s => ({
              id: crypto.randomUUID(),
              supplierId: s.id,
              name: s.name,
              hasLoa: false,
              loaLink: '',
            })),
        )
        setOptions(optionsRes.data.list)
        setLoading(false)
      })
      .catch(err => {
        console.error('Failed to load manufacturer suppliers', err)
        setLoading(false)
      })
  }, [open, manufacturerId])

  const handleAddNew = () => {
    setRows(prev => [{ id: crypto.randomUUID(), supplierId: null, name: '', hasLoa: false, loaLink: '' }, ...prev])
  }

  const save = async (current: SupplierRowDraft[]) => {
    if (manufacturerId === null) return
    const sorted = [...current].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
    setRows(sorted)
    const supplierIds = sorted.map(r => r.supplierId).filter((id): id is number => id !== null)
    try {
      await axios.post(`/manufacturer/${manufacturerId}/suppliers`, { supplierIds })
    } catch (err) {
      console.error('Failed to update manufacturer suppliers', err)
    }
  }

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return
    const updated = rows.filter(r => r.id !== deleteTarget.id)
    save(updated)
    setDeleteTarget(null)
  }

  return (
    <>
      <Drawer className="side-drawer" anchor="right" open={open} onClose={onClose}>
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Authorized Suppliers
            </Typography>
            <Button variant="contained" size="small" startIcon={<Add />} onClick={handleAddNew}>
              ADD NEW
            </Button>
          </Box>

          <IconButton onClick={onClose} size="small" aria-label="Close authorized suppliers drawer">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              elevation={1}
              sx={{ borderRadius: 1, border: '1px solid rgba(0,0,0,0.12)' }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: '#3b5685',
                      '& th': { color: '#fff', fontWeight: 600, bgcolor: '#3b5685' },
                    }}
                  >
                    <TableCell>Supplier</TableCell>
                    <TableCell>LOA</TableCell>
                    <TableCell>LOA Link</TableCell>
                    <TableCell sx={{ width: 56 }} />
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          No authorized suppliers. Click "+ ADD NEW" to add one.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map(row => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Autocomplete
                            size="small"
                            options={options.filter(o => !rows.some(r => r.supplierId === o.id && r.id !== row.id))}
                            getOptionLabel={o => o.name}
                            value={options.find(o => o.id === row.supplierId) ?? null}
                            onChange={(_e, newValue) => {
                              const updated = {
                                ...row,
                                supplierId: newValue?.id ?? null,
                                name: newValue?.name ?? '',
                              }
                              const newRows = rows.map(r => (r.id === row.id ? updated : r))
                              if (newValue) save(newRows)
                              else setRows(newRows)
                            }}
                            renderInput={params => <TextField {...params} placeholder="Supplier" />}
                          />
                        </TableCell>

                        <TableCell>
                          <Checkbox size="small" disabled checked={row.hasLoa} />
                        </TableCell>

                        <TableCell>
                          <Link component="button" variant="body2" underline="hover" disabled>
                            Link
                          </Link>
                        </TableCell>

                        <TableCell>
                          <IconButton
                            size="small"
                            aria-label="Delete supplier row"
                            onClick={() => setDeleteTarget(row)}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Drawer>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete Authorized Supplier?"
        body="Are you sure you want to delete this authorized supplier? The action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  )
}
